import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";

import AmountField from "../../Components/Form Fields/amount-field";
import PrimaryButton from "../../Components/Buttons/primary-button";

import renderAuthorizeVenmoTransfer from "../../Organisms/Modals/Transfer/authorize-venmo-transfer";

import { useActivities, useDeals, useUserAccount } from "../../CustomHooks/Queries";

import withFormProvider from "../../HOC/withFormProvider";

import { useModalContext } from "../../Contexts/ModalContext";

import { dispatchErrorToast, formatCurrency, transformCountryCodeFormatToUSNumber } from "../../Utils/helpers";

import { VenmoMark } from "../../Assets/Images";
import { ChevronLeft } from "../../Assets/Icons";

function VenmoTransfer() {
  const modalContext = useModalContext();

  const navigate = useNavigate();

  const { watch, setValue, handleSubmit } = useFormContext();

  const { isLoading: isLoadingDeals, data: deals } = useDeals();

  const { isLoading: isLoadingActivities, data: activitiesData } = useActivities();

  const { isLoading: isLoadingUserAccountData, data: userAccountData } = useUserAccount();

  const dailyCashOutLimit = useMemo(() => deals?.data?.content?.transfer_out_limit, [deals]);

  const phoneNumber = useMemo(
    () => transformCountryCodeFormatToUSNumber(userAccountData?.data?.data?.user?.phoneNumber ?? ""),
    [userAccountData]
  );

  const cashAmount = parseFloat(watch("cash-amount")?.unmasked ?? "");
  const agreeToTerms = watch("agreeToTerms") ?? false;

  const disableSubmitButton = isNaN(cashAmount) || cashAmount === 0 || !agreeToTerms;

  const onSubmit = () => {
    if (dailyCashOutLimit > 0 && cashAmount > dailyCashOutLimit) {
      dispatchErrorToast("Amount cannot exceed the daily limit.", {
        alignToAppContent: true,
        key: "amountExceedsDailyLimit",
      });
    } else if (cashAmount > (activitiesData?.data?.data?.currentBalance ?? 0)) {
      dispatchErrorToast("Amount exceeds what you have available to transfer.", {
        alignToAppContent: true,
        key: "amountExceedsAvailableFunds",
      });
    } else {
      renderAuthorizeVenmoTransfer(modalContext, { cashAmount });
    }
  };

  const isLoading = isLoadingDeals || isLoadingActivities || isLoadingUserAccountData;

  const [animReady, setAnimReady] = useState(false);
  const hasTriggeredAnim = useRef(false);

  useEffect(() => {
    if (!isLoading && !hasTriggeredAnim.current) {
      hasTriggeredAnim.current = true;
      requestAnimationFrame(() => setAnimReady(true));
    }
  }, [isLoading]);

  return (
    <div className={`venmo-transfer-alt${animReady ? " anim-ready" : ""}`}>
      <div className="vt-header">
        <button className="vt-back" onClick={navigate.bind(this, -1)} type="button" aria-label="Go back">
          <ChevronLeft />
        </button>
        <h1 className="vt-title">Cash Out with Venmo</h1>
      </div>

      {isLoading ? (
        <div className="vt-loading">
          <CircularProgress />
        </div>
      ) : (
        <form className="vt-body" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <div className="vt-amount-section">
            <AmountField />
            {!!dailyCashOutLimit && (
              <span className="vt-daily-limit">Daily limit: {formatCurrency(dailyCashOutLimit)}</span>
            )}
          </div>

          <div className="vt-transfer-flow">
            <div className="vt-endpoint-card">
              <span className="vt-endpoint-label">From</span>
              <div className="vt-endpoint-icon vt-stake-icon">
                <span>S</span>
              </div>
              <span className="vt-endpoint-name">Stake</span>
              <span className="vt-endpoint-detail">
                {formatCurrency(activitiesData?.data?.data?.currentBalance ?? 0)}
              </span>
            </div>

            <svg className="vt-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 10H16" stroke="#636879" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M12 6L16 10L12 14" stroke="#636879" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>

            <div className="vt-endpoint-card">
              <span className="vt-endpoint-label">To</span>
              <div className="vt-endpoint-icon vt-venmo-icon">
                <VenmoMark className="venmo-mark" />
              </div>
              <span className="vt-endpoint-name">Venmo</span>
              <span className="vt-endpoint-detail">{phoneNumber}</span>
            </div>
          </div>

          <div className="vt-details-card">
            <div className="vt-detail-row">
              <span className="vt-detail-label">Fee</span>
              <span className="vt-detail-value-accent">Free</span>
            </div>
            <div className="vt-detail-divider" />
            <div className="vt-detail-row vt-consent-row">
              <button
                type="button"
                className={`vt-checkbox${agreeToTerms ? " checked" : ""}`}
                onClick={() => setValue("agreeToTerms", !agreeToTerms)}
              >
                <svg viewBox="0 0 12 12" fill="none">
                  <path d="M2 6L5 9L10 3" stroke="#0F1219" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <span className="vt-consent-text">
                I understand this transfer will be sent to the Venmo account associated with {phoneNumber}.
              </span>
            </div>
          </div>

          <PrimaryButton className="vt-submit-btn" type="submit" disabled={disableSubmitButton}>
            Transfer
          </PrimaryButton>
        </form>
      )}
    </div>
  );
}

export default withFormProvider(VenmoTransfer);
